import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "react-hot-toast";
import PersonalInfoPage from "./profileComponents/personalInfoPage";
import { AddressPage } from "./profileComponents/addressPage";
import { WorkExperiencePage } from "./profileComponents/workExperiencePage";
import { EducationPage } from "./profileComponents/educationPage";
import { PermissionPage } from "./profileComponents/permissionPage";
import { NotesPage } from "./profileComponents/notePage";
import { StatusPage } from "./profileComponents/statusPage";
import { NotificationPage } from "./profileComponents/notifiactionPage";
import VoiceSendModal from "./profileComponents/voiceSendModal";
import { StoragePage } from "./profileComponents/storagePage";
import { PaymentPage } from "./profileComponents/paymentPage";
import { ContactLimitPage } from "./profileComponents/contactLimitPage";
import { IntroductionPage } from "./profileComponents/introductionPage";
import ImageUploadModal from "./profileComponents/ImageUploadModal";
import { ModalWrapper } from "./chatScreen/chatbar";
import { openAppTour } from "./common/TourGuideGate";
import { isProfileSetupComplete } from "./common/ProfileCompletionModal";
import usePaymentStatus from "../hooks/usePaymentStatus";
import { appStorage } from "../utils/appStorage";
import { forceAuthLogout } from "../utils/authSession";
import {
  isPostSignupIntroActive,
  shouldRedirectToContactAfterProfile,
  clearPostSignupAwaitContact,
  OPEN_INTRODUCTION_ONBOARDING_EVENT,
} from "../utils/postSignupOnboarding";

// FIX #10: Skeleton now uses flex layout to match real layout side-by-side
const ProfileSkeleton = () => (
  <div className="flex gap-4">
    <div className="hidden min-[900px]:block min-[900px]:w-[360px] min-[900px]:min-w-[300px] min-[900px]:flex-shrink-0 space-y-4">
      <div className="flex flex-col justify-center items-center p-4 rounded-2xl shadow-sm">
        <div className="h-24 w-24 rounded-full bg-gray-200 animate-pulse" />
        <div className="mt-3 h-4 w-20 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div key={idx} className="h-12 bg-gray-200 rounded-xl animate-pulse" />
        ))}
      </div>
    </div>
    <div className="w-full min-[900px]:flex-1 min-w-0 space-y-4">
      <div className="rounded-2xl p-6 space-y-4">
        <div className="h-6 w-40 bg-gray-200 rounded animate-pulse mb-6" />
        {Array.from({ length: 5 }).map((_, idx) => (
          <div key={idx} className="space-y-2">
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
            <div className="h-11 bg-gray-200 rounded-lg animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Delete Account Confirmation Modal
const DeleteAccountModal = ({ open, onClose, onConfirm, loading }) => {
  if (!open) return null;
  return (
    <ModalWrapper onClose={onClose}>
      <div className="text-center">
        <div className="mb-4 flex justify-center">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
            <img src="../delete.png" alt="Delete Account" className="h-8 w-8" />
          </div>
        </div>
        <h2 className="text-xl font-semibold mb-2 text-gray-900">Delete Account?</h2>
        <p className="text-sm text-gray-600 mb-6">
    You can retrieve your account upto 30 days. All your data will then be permanently deleted and this action can not be undone. 

        </p>
      </div>
      <button
        className="w-full bg-red-600 text-white font-semibold py-3 rounded-[25px] hover:bg-red-700 transition disabled:opacity-50"
        onClick={onConfirm}
        disabled={loading}
      >
        {loading ? "Deleting..." : "Yes, Delete Account"}
      </button>
      <button className="w-full submit-button mt-3" onClick={onClose} disabled={loading}>
        Cancel
      </button>
    </ModalWrapper>
  );
};

// Constants
const SECTIONS = [
  "Personal Info",
  "Work Experience",
  "Education",
  "Payment",
  "Status",
  "Settings",
  // "Storage",
 
  // "Notes",
];

const SETTINGS_SUBSECTIONS = ["Permission", "Notification", "Storage", "Blocked User", "Introduction"];

const SETTINGS_DISPLAY_LABELS = {
  Permission: "Permission",
  Notification: "Notification",
  Storage: "My Usage",
  "Blocked User": "Blocked Users",
  Introduction: "Introduction",
};

const PERMISSION_SECTIONS = ["Personal Info", "Work Experience", "Education"];

const PROFILE_MANDATORY_STEPS = [
  {
    key: "plan_personal_info",
    label: "Personal information",
    description: "Name, contact and home address details",
    section: "Personal Info",
    personalInfoTarget: "basic",
  },
  {
    key: "plan_work_experience",
    label: "Work experience",
    description: "Employment history",
    section: "Work Experience",
  },
  {
    key: "plan_education",
    label: "Education",
    description: "Schools and qualifications",
    section: "Education",
  },
];

const DEFAULT_USER_INFO = {
  name: "--",
  phone: "+00 00000 00000",
  email: "xyz@gmail.com",
  avatar: "./profile-picture.png",
  rawProfile: null,
  // FIX #1: user_id field added
  user_id: null,
  // NEW: track all profile images for multi-image plans
  profileImages: [],
};

const SECTION_ICONS = {
  "Personal Info": "./new-personal.svg",
  "Work Experience": "./new-work.svg",
  Education: "./new-edu.svg",
  Payment: "./crown-color.svg",
  Status: "./input-logos/clock.svg",
  Settings: "./setting.svg",
  Notes: "./note.svg",
  Permission: "./settings/permission.svg",
  Notification: "./color-notification.svg",
  Storage: "./crown-color.svg",
  "Blocked User": "/blocked.svg",
  Introduction: "/settings/introduction.svg",
};

const getSectionIcon = (label) => SECTION_ICONS[label] || null;
const getSectionDisplayLabel = (label) => SETTINGS_DISPLAY_LABELS[label] || label;

const BUTTON_STYLES = {
  base: "group flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-medium text-left transition",
  active: "border-[#6C45FF] bg-[#6C45FF] text-white shadow-[0_8px_24px_rgba(108,69,255,0.22)]",
  inactive:
    "border-[#EEE6FF] bg-white text-gray-800 hover:border-[#D6C5FF] hover:bg-[#FAF8FF] hover:shadow-[0_8px_24px_rgba(108,69,255,0.08)]",
};

const SectionItem = React.memo(({ label, active, onClick, disabled, icon }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    aria-label={`Open ${label}`}
    disabled={disabled}
    className={`${BUTTON_STYLES.base} ${active ? BUTTON_STYLES.active : BUTTON_STYLES.inactive} ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
  >
    {icon ? (
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
          active ? "bg-white" : "bg-[#F1F1F3]"
        }`}
      >
        <img src={icon} alt="" aria-hidden="true" className="h-5 w-5 object-contain" />
      </span>
    ) : null}
    <span className="min-w-0 flex-1 font-bold text-base sm:text-lg">{label}</span>
    <img
      src={active ? "./chatbar/white-arrow.svg" : "./chatbar/dark-arrow.svg"}
      alt=""
      aria-hidden="true"
      className="h-5 w-5 shrink-0 transition group-hover:translate-x-0.5 sm:h-6 sm:w-6"
    />
  </button>
));

// UserProfile: shows avatar and allows picking an image
// NEW: planType controls single vs multi-image upload behaviour
const UserProfile = React.memo(({
  userInfo,
  profilePreviewUrl,
  onUpload,
  uploading,
  handleSectionClick,
  planType,
  onOpenImageModal,
  onOpenQrModal,
  onOpenProfileGallery,
}) => {
  const fileInputRef = React.useRef(null);
  const audioRef = React.useRef(null);
  const [isPlaying, setIsPlaying] = React.useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onUpload) onUpload(file, []);
    e.target.value = "";
  };

  // NEW: plan 1 → direct file input; plan 2/3 → open multi-image modal
  const handleEditPhotoClick = () => {
    if (planType >= 2) {
      onOpenImageModal();
    } else {
      fileInputRef.current?.click();
    }
  };

  const handlePlayAudio = () => {
    const voiceUrl = userInfo?.rawProfile?.personal_info?.voice_full_name;
    if (!voiceUrl) return;
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      audioRef.current?.play();
      setIsPlaying(true);
    }
  };

  const voiceUrl = userInfo?.rawProfile?.personal_info?.voice_full_name;

  return (
    <div>
      <div className="font-semibold px-4 sm:px-0">
        <p className="text-center text-sm sm:text-lg">User Profile</p>
      </div>

      <div className="flex max-[1150px]:flex-col max-[1150px]:items-center items-start gap-4 md:gap-6 p-4 sm:p-5 rounded-2xl">
        <div className="relative flex-shrink-0">
          <button
            type="button"
            onClick={onOpenProfileGallery}
            className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6C45FF] focus-visible:ring-offset-2"
            aria-label="Open profile image gallery"
          >
            <img
              src={profilePreviewUrl || userInfo?.avatar || DEFAULT_USER_INFO.avatar}
              alt={userInfo?.name || DEFAULT_USER_INFO.name}
              className="h-20 w-20 sm:h-24 sm:w-24 min-[900px]:h-28 min-[900px]:w-28 rounded-full object-cover border-2 border-[#6938EF] shadow-md"
            />
          </button>

          <button
            type="button"
            onClick={onOpenQrModal}
            className="absolute -bottom-1 -right-1 rounded-full bg-white p-1 shadow-md border border-[#E6E2FF] hover:scale-105 transition"
            title="Open QR Code"
            aria-label="Open QR Code"
          >
            <img src="./chatbar/qr-code-icon.svg" alt="QR" className="h-6 w-6" />
          </button>
        </div>

        <div className="text-xs sm:text-sm min-w-0 space-y-1 w-full max-[900px]:flex max-[900px]:flex-col max-[900px]:items-center">
          <p className="font-semibold text-gray-900 text-sm sm:text-base md:text-lg aoboshi-one flex items-center gap-2 w-full max-[900px]:justify-center">
            <span className="truncate">{userInfo?.name || DEFAULT_USER_INFO.name}</span>
            {voiceUrl && (
              <button
                type="button"
                onClick={handlePlayAudio}
                className="cursor-pointer hover:opacity-70 transition flex-shrink-0"
                title={isPlaying ? "Pause" : "Play"}
              >
                <img
                  src={isPlaying ? "../chatscreen/pause.svg" : "../chatscreen/speaker.svg"}
                  alt={isPlaying ? "Pause" : "Play"}
                  className="w-4 h-4"
                />
              </button>
            )}
          </p>
          {voiceUrl && (
            <audio
              ref={audioRef}
              src={`${process.env.REACT_APP_API_BASE_URL}${voiceUrl}`}
              onEnded={() => setIsPlaying(false)}
            />
          )}
          <p className="text-gray-700 text-sm sm:text-base font-medium max-[900px]:text-center">
            {userInfo?.phone || DEFAULT_USER_INFO.phone}
          </p>
          <p className="text-gray-600 text-sm sm:text-base break-all truncate max-[900px]:text-center max-w-full">
            {userInfo?.email || DEFAULT_USER_INFO.email}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between w-full px-4 sm:px-5">
        <button
          type="button"
          onClick={handleEditPhotoClick}
          className="text-sm text-[#6366F1] font-semibold hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
          disabled={uploading}
        >
          {uploading ? "Uploading..." : "Edit Photo"}
        </button>
        <button
          type="button"
          onClick={() => handleSectionClick("Notes")}
          className="text-sm text-[#6366F1] font-semibold hover:underline"
        >
          Add Note
        </button>
      </div>

      {planType < 2 && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          aria-hidden
        />
      )}
    </div>
  );
});

const BackButton = React.memo(({ onBack, label = "Permissions" }) => (
  <button
    onClick={onBack}
    aria-label={`Back to ${label}`}
    className="flex items-center aoboshi-one text-lg text-[#212121] font-medium w-full p-4 rounded-2xl !focus-visible:shadow-none"
  >
    <img src="./back-arrow.svg" alt="back-arrow" className="mr-4" />
    <span className="flex-1 text-center">{label}</span>
  </button>
));

const BlockedUsersPage = React.memo(() => {
  const [loading, setLoading] = useState(true);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [unblockingUserId, setUnblockingUserId] = useState("");

  const normalizeBlockedUsers = useCallback((payload) => {
    const source = Array.isArray(payload?.data)
      ? payload.data.flatMap((group) => (Array.isArray(group?.blocked_users) ? group.blocked_users : []))
      : [];

    return source
      .map((item, index) => {
        if (!item) return null;
        const name = item.full_name || [item.first_name, item.last_name].filter(Boolean).join(" ") || "Unknown";
        const phone = [item.country_code, item.phone_number].filter(Boolean).join(" ").trim();

        return {
          id: item.user_id || item.id || item.friend_id || `blocked-${index}`,
          name,
          phone,
          profileImage: item.profile_image
            ? `${process.env.REACT_APP_API_BASE_URL}${item.profile_image}`
            : "",
        };
      })
      .filter(Boolean);
  }, []);

  const fetchBlockedUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = Cookies.get("auth_token");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        window.location.href = "/login";
        return;
      }

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/block/list`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data?.status !== "1") {
        throw new Error(data?.message || "Failed to load blocked users");
      }

      setBlockedUsers(normalizeBlockedUsers(data));
    } catch (err) {
      toast.error(err?.message || "Failed to load blocked users");
      setBlockedUsers([]);
    } finally {
      setLoading(false);
    }
  }, [normalizeBlockedUsers]);

  const handleUnblockUser = useCallback(async (targetUserId) => {
    try {
      const token = Cookies.get("auth_token");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        window.location.href = "/login";
        return;
      }

      setUnblockingUserId(targetUserId);

      const body = new URLSearchParams();
      body.append("user_id", targetUserId);

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/unblock`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
      });

      const data = await res.json();
      if (data?.status !== "1") {
        throw new Error(data?.message || "Failed to unblock user");
      }

      toast.success(data?.message || "User unblocked successfully");
      setBlockedUsers((prev) => prev.filter((user) => user.id !== targetUserId));
    } catch (err) {
      toast.error(err?.message || "Failed to unblock user");
    } finally {
      setUnblockingUserId("");
    }
  }, []);

  useEffect(() => {
    fetchBlockedUsers();
  }, [fetchBlockedUsers]);

  if (loading) {
    return (
      <div className="rounded-2xl p-6 bg-white border border-gray-100">
        <div className="h-5 w-36 bg-gray-200 rounded animate-pulse mb-4" />
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="h-12 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl p-6 bg-white border border-gray-100">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Blocked Users</h2>
      {blockedUsers.length === 0 ? (
        <p className="text-sm text-gray-500">No blocked users found.</p>
      ) : (
        <div className="space-y-2">
          {blockedUsers.map((user) => (
            <div
              key={user.id}
              className="px-4 py-3 rounded-xl border border-gray-100 bg-gray-50 text-sm text-gray-800 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="font-medium text-gray-900 truncate">{user.name}</div>
                {user.phone ? <div className="text-xs text-gray-500 truncate">{user.phone}</div> : null}
              </div>
              <button
                type="button"
                onClick={() => handleUnblockUser(user.id)}
                disabled={unblockingUserId === user.id}
                className="text-xs px-3 py-1.5 rounded-full bg-[#6C45FF] text-white hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {unblockingUserId === user.id ? "Unblocking..." : "Unblock"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

const SettingsContent = React.memo(({ activeTab, permissionSubTab, onPermissionSubTabClick, introductionSubTab, onIntroductionSubTabChange }) => {
  if (!activeTab)
    return (
      <div className="hidden md:flex flex-col items-center justify-center min-h-[calc(100vh-100px)] gap-4 bg-[#F4F3FF]">
        <div className="h-40 w-40 rounded-xl bg-gradient-to-br from-[#7C4DFF] to-[#6A35FF] flex items-center justify-center shadow-md">
          <img src="./logo.svg" alt=" PhoneBookPlus Logo" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-500 font-inter">
           PhoneBookPlus
        </h1>
      </div>
    );
  if (activeTab === "Introduction") {
    return (
      <IntroductionPage
        activeSubTab={introductionSubTab}
        onSubTabChange={onIntroductionSubTabChange}
      />
    );
  }
  if (activeTab === "Permission") {
    if (permissionSubTab) {
      return <PermissionPage activeTab={permissionSubTab} />;
    }
    return (
      // <div className="space-y-3 p-4">
      //   <div className="font-semibold mb-2">Select Permission Section:</div>
      //   {PERMISSION_SECTIONS.map((section) => (
      //     <button
      //       key={section}
      //       className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition bg-white text-gray-800 hover:bg-gray-50 border border-gray-100"
      //       onClick={() => onPermissionSubTabClick(section)}
      //     >
      //       <span className="font-bold text-lg">{section}</span>
      //     </button>
      //   ))}
      // </div>/
      <></>
    );
  }
  if (activeTab === "Notification") return <NotificationPage />;
  if (activeTab === "Storage") return <ContactLimitPage />;
  if (activeTab === "Blocked User") return <BlockedUsersPage />;
  return null;
});

const MenuButton = React.memo(({ onToggleSidebar }) => (
            <div className="min-[900px]:hidden mb-4">
    <button
      onClick={onToggleSidebar}
      className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-100 hover:bg-gray-50 transition"
      aria-label="Back to menu"
    >
      <img src="./back-arrow.svg" alt="" className="w-5 h-5" />
      <span className="font-medium">Back</span>
    </button>
  </div>
));

const normalizePlanFlag = (value, fallback = 1) => {
  if (value === undefined || value === null || value === "") return fallback;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? fallback : (numeric === 1 ? 1 : 0);
};

const getPlanFlagsFromProfile = (profile = {}) => ({
  plan_personal_info:
    normalizePlanFlag(profile.plan_personal_info, 1) === 1 &&
    normalizePlanFlag(profile.plan_home_address, 1) === 1
      ? 1
      : 0,
  plan_home_address: normalizePlanFlag(profile.plan_home_address, 1),
  plan_work_experience: normalizePlanFlag(profile.plan_work_experience, 1),
  plan_education: normalizePlanFlag(profile.plan_education, 1),
});

const ProfileImageSliderModal = React.memo(({
  open,
  images,
  currentIndex,
  onClose,
  onPrev,
  onNext,
  onTouchStart,
  onTouchEnd,
  loading,
}) => {
  if (!open) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 z-[26000] bg-black/75 flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="w-full max-w-xl rounded-2xl bg-[#fff] p-6 sm:p-8 flex flex-col items-center justify-center min-h-[300px] sm:min-h-[420px]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="h-10 w-10 border-4 border-gray-200 border-t-[#6C45FF] rounded-full animate-spin" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading images...</p>
        </div>
      </div>
    );
  }

  if (!Array.isArray(images) || images.length === 0) return null;

  const safeIndex = Math.min(Math.max(currentIndex, 0), images.length - 1);
  const current = images[safeIndex];

  return (
    <div className="fixed inset-0 z-[26000] bg-black/75 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="relative w-full max-w-xl rounded-2xl bg-[#fff] p-1 sm:p-1"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-20 h-9 w-9 rounded-full bg-black/20 text-white text-lg leading-none "
          aria-label="Close image preview"
        >
          x
        </button>

        <div className="relative flex items-center justify-center min-h-[300px] sm:min-h-[420px]">
          {images.length > 1 && (
            <button
              type="button"
              onClick={onPrev}
              className="absolute left-2 sm:left-3 z-10 h-10 w-10 rounded-full bg-black/25 text-white "
              aria-label="Previous image"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 mx-auto" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
          )}

          <img
            src={current?.url || DEFAULT_USER_INFO.avatar}
            alt={`Profile image ${safeIndex + 1}` || "Profile image"}
            className="max-h-[70vh] w-full object-contain rounded-xl"
            onError={(e) => {
              e.currentTarget.src = DEFAULT_USER_INFO.avatar;
            }}
          />

          {images.length > 1 && (
            <button
              type="button"
              onClick={onNext}
              className="absolute right-2 sm:right-3 z-10 h-10 w-10 rounded-full bg-black/25 text-white"
              aria-label="Next image"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 mx-auto" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          )}
        </div>

        {images.length > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2">
            {images.map((_, idx) => (
              <span
                key={idx}
                className={`h-2 w-2 rounded-full ${idx === safeIndex ? "bg-white/25" : "bg-white/40"}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

// Helper: normalise profile images from API response into a consistent shape
const normalizeProfileImages = (profile) => {
  // New API shape: profile.profile_images_by_tag = [{ image_url, tag, uploaded }]
  if (Array.isArray(profile.profile_images_by_tag)) {
    return profile.profile_images_by_tag.map((item) => ({
      url: `${process.env.REACT_APP_API_BASE_URL}${item.image_url}`,
      tags: Array.isArray(item.tags) ? item.tags : [],
      uploaded: Boolean(item.uploaded),
      imageId: item.image_id || item.imageId || item.id || null,
    }));
  }

  // Backwards-compatible: older APIs returned profile_images as an array
  if (Array.isArray(profile.profile_images)) {
    return profile.profile_images.map((img) => ({
      url: `${process.env.REACT_APP_API_BASE_URL}${img.image || img.profile_image || img}`,
      tags: Array.isArray(img.tags) ? img.tags : [],
    }));
  }

  if (profile.profile_image) {
    return [{ url: `${process.env.REACT_APP_API_BASE_URL}${profile.profile_image}`, tags: [] }];
  }

  return [];
};

const normalizeGalleryImages = (payload) => {
  const source = Array.isArray(payload?.profile_images_by_tag)
    ? payload.profile_images_by_tag
    : Array.isArray(payload?.profile_images)
      ? payload.profile_images
      : Array.isArray(payload?.images)
        ? payload.images
        : payload?.profile_image
          ? [payload]
          : [];

  return source
    .map((item) => {
      const rawUrl = item?.image_url || item?.image || item?.profile_image || item?.url || item;
      if (!rawUrl) return null;
      const url = String(rawUrl).startsWith("http")
        ? String(rawUrl)
        : `${process.env.REACT_APP_API_BASE_URL}${rawUrl}`;
      return {
        url,
        tags: Array.isArray(item?.tags) ? item.tags : [],
      };
    })
    .filter(Boolean);
};

export default function PersonalInfo() {
  const navigate = useNavigate();
  const { planType, loading: paymentStatusLoading } = usePaymentStatus();

  const [activeSection, setActiveSection] = useState(() => {
    try {
      return appStorage.getItem("active_section") || "Personal Info";
    } catch (e) {
      return "Personal Info";
    }
  });
  const [showSidebar, setShowSidebar] = useState(true);
  const [userInfo, setUserInfo] = useState(DEFAULT_USER_INFO);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
  const profileContentPanelRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [downloadingQr, setDownloadingQr] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [activeSettingSubsection, setActiveSettingSubsection] = useState(() => {
    try {
      const v = appStorage.getItem("active_setting_subsection");
      return v && v.length ? v : null;
    } catch (e) {
      return null;
    }
  });
  const [activePermissionSubTab, setActivePermissionSubTab] = useState(() => {
    try {
      const v = appStorage.getItem("active_permission_subtab");
      return v && v.length ? v : null;
    } catch (e) {
      return null;
    }
  });
  const [activeIntroductionSubTab, setActiveIntroductionSubTab] = useState(() => {
    try {
      const v = appStorage.getItem("active_introduction_subtab");
      return v && v.length ? v : null;
    } catch (e) {
      return null;
    }
  });
  // NEW: controls multi-image modal visibility
  const [showImageModal, setShowImageModal] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );
  const [planCompletionFlags, setPlanCompletionFlags] = useState({
    plan_personal_info: 1,
    plan_home_address: 1,
    plan_work_experience: 1,
    plan_education: 1,
  });
  const [personalInfoFocusTarget, setPersonalInfoFocusTarget] = useState({
    section: "basic",
    nonce: 0,
  });
  const [showProfileGalleryModal, setShowProfileGalleryModal] = useState(false);
  const [currentProfileImageIndex, setCurrentProfileImageIndex] = useState(0);
  const [profileGalleryImages, setProfileGalleryImages] = useState([{ url: DEFAULT_USER_INFO.avatar, tags: [] }]);
  const [profileGalleryLoading, setProfileGalleryLoading] = useState(false);
  const touchStartXRef = useRef(null);
  const activeSectionRef = useRef(activeSection);
  const [profileCompletionLock, setProfileCompletionLock] = useState({
    enabled: false,
    section: null,
    personalInfoTarget: null,
  });

  // FIX #8: consolidated into a single matchMedia listener (was two separate listeners)
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 899px)");
    const handler = (e) => {
      setIsMobile(e.matches);
      if (!e.matches) setShowSidebar(true);
    };
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else mql.addListener(handler);
    setIsMobile(mql.matches);
    if (!mql.matches) setShowSidebar(true);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else mql.removeListener(handler);
    };
  }, []);

  useEffect(() => {
    activeSectionRef.current = activeSection;
  }, [activeSection]);

  const handleProfileSidebarFocus = useCallback(() => {
    setShowSidebar(true);
    if (activeSectionRef.current === "Settings") {
      setActiveSection("Personal Info");
      setActiveSettingSubsection(null);
      setActivePermissionSubTab(null);
      setActiveIntroductionSubTab(null);
      try {
        appStorage.setItem("active_section", "Personal Info");
        appStorage.removeItem("active_setting_subsection");
        appStorage.removeItem("active_permission_subtab");
        appStorage.removeItem("active_introduction_subtab");
      } catch (e) {
        // ignore
      }
    }
  }, []);

  const handleDeleteAccount = async () => {
    try {
      setDeletingAccount(true);
      const user_id = appStorage.getItem("user_id");
      const email = appStorage.getItem("email");
      if (!user_id || !email) {
        toast.error("User ID or email not found in session storage");
        return;
      }
      const token = Cookies.get("auth_token");
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/account/delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: new URLSearchParams({ user_id, email }).toString(),
      });
      const data = await res.json();
      if (data.status === "1") {
        toast.success("Account deleted successfully");
        setShowDeleteModal(false);
        Cookies.remove("auth_token");
        forceAuthLogout();
        window.location.href = "/login";
      } else {
        toast.error(data.message || "Failed to delete account");
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete account");
    } finally {
      setDeletingAccount(false);
    }
  };

  // NEW: accepts optional tags array for multi-image plan uploads
  const handleUploadProfileImage = useCallback(async (file, tags = []) => {
    try {
      setUploadingImage(true);
      const token = Cookies.get("auth_token");

      if (!token) {
        toast.dismiss();
        toast.error("Session expired. Please log in again.");
        return (window.location.href = "/login");
      }

      const fd = new FormData();
      fd.append("image", file);
      // API accepts comma-separated tags: e.g. "Friend,Work" (Basic Plus / Pro plans)
      // Basic plan ignores tag entirely — safe to always send
      if (tags.length > 0) {
        fd.append("tag", tags.join(","));
      }

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/upload-profile-image`, {
        method: "POST",
        body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (data?.status === "1") {
        const profile = data?.data?.[0]?.profile || data?.profile || {};

        setUserInfo((prev) => ({
          ...prev,
          name: `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || prev.name,
          phone: profile.phone_number
            ? `${profile.country_code || ""}${profile.phone_number}`
            : prev.phone,
          email: profile.email || prev.email,
          avatar: profile.profile_image
            ? `${process.env.REACT_APP_API_BASE_URL}${profile.profile_image}`
            : prev.avatar,
          rawProfile: profile || prev.rawProfile,
          // FIX #1: persist user_id from API response
          user_id: profile.user_id || profile.id || prev.user_id,
          // NEW: update full images list so modal reflects latest state
          profileImages: normalizeProfileImages(profile),
        }));
        setProfileGalleryImages(normalizeGalleryImages(profile));

        toast.success("Profile photo updated");
      } else {
        toast.dismiss();
        toast.error(data?.message || "Upload failed");
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err?.message || "Upload failed");
    } finally {
      setUploadingImage(false);
    }
  }, []);

  // Keep sidebar avatar in sync with modal image changes (remove/tag/default updates)
  const handleProfileImagesChange = useCallback((imagesPayload) => {
    if (!imagesPayload) return;

    setUserInfo((prev) => ({
      ...prev,
      avatar: imagesPayload.profile_image
        ? `${process.env.REACT_APP_API_BASE_URL}${imagesPayload.profile_image}`
        : prev.avatar,
      rawProfile: prev.rawProfile
        ? {
            ...prev.rawProfile,
            profile_image: imagesPayload.profile_image || prev.rawProfile.profile_image,
            profile_images_by_tag: Array.isArray(imagesPayload.profile_images_by_tag)
              ? imagesPayload.profile_images_by_tag
              : prev.rawProfile.profile_images_by_tag,
            default_profile_image:
              imagesPayload.default_profile_image || prev.rawProfile.default_profile_image,
          }
        : prev.rawProfile,
      profileImages: Array.isArray(imagesPayload.profile_images_by_tag)
        ? imagesPayload.profile_images_by_tag.map((item) => ({
            url: `${process.env.REACT_APP_API_BASE_URL}${item.image_url}`,
            tags: Array.isArray(item.tags) ? item.tags : [],
            uploaded: Boolean(item.uploaded),
            imageId: item.image_id || item.imageId || item.id || null,
          }))
        : prev.profileImages,
    }));
    setProfileGalleryImages(normalizeGalleryImages(imagesPayload));
  }, []);

  const handleSectionClick = useCallback(
    (section) => {
      if (profileCompletionLock.enabled && profileCompletionLock.section && profileCompletionLock.section !== section) {
        return;
      }
      setActiveSection(section);
      try { appStorage.setItem("active_section", section); } catch (e) {}
      // clear permission/subsection when switching sections
      setActivePermissionSubTab(null);
      try { appStorage.removeItem("active_permission_subtab"); } catch (e) {}
      setActiveIntroductionSubTab(null);
      try { appStorage.removeItem("active_introduction_subtab"); } catch (e) {}
      setActiveSettingSubsection(null);
      try { appStorage.removeItem("active_setting_subsection"); } catch (e) {}
      if (section !== "Settings" && isMobile) {
        setShowSidebar(false);
      }
    },
    [isMobile, profileCompletionLock.enabled, profileCompletionLock.section]
  );

  // Layered back navigation (mobile must return to sidebar at each level):
  //   - permission section  → permission menu
  //   - introduction guide  → introduction menu
  //   - permission menu     → settings menu
  //   - settings leaf       → mobile: leave Settings; desktop: settings menu (unchanged)
  //   - settings root       → main profile
  const handleBack = useCallback(() => {
    // During post-signup Introduction, only Skip proceeds — don't leave the guide early
    if (
      isPostSignupIntroActive() &&
      !activePermissionSubTab &&
      !activeIntroductionSubTab &&
      activeSettingSubsection === "Introduction"
    ) {
      return;
    }

    if (activePermissionSubTab) {
      setActivePermissionSubTab(null);
      try { appStorage.removeItem("active_permission_subtab"); } catch (e) {}
      if (isMobile) {
        // Mobile: stay on Permission menu (Personal Info / Work / Education)
        setShowSidebar(true);
      } else {
        // Desktop: keep previous behavior — leave Permission drill-down entirely
        setActiveSettingSubsection(null);
        try { appStorage.removeItem("active_setting_subsection"); } catch (e) {}
      }
      return;
    }

    if (activeIntroductionSubTab) {
      setActiveIntroductionSubTab(null);
      try { appStorage.removeItem("active_introduction_subtab"); } catch (e) {}
      if (isMobile) setShowSidebar(true);
      return;
    }

    // Permission menu → Settings menu
    if (activeSettingSubsection === "Permission") {
      setActiveSettingSubsection(null);
      try { appStorage.removeItem("active_setting_subsection"); } catch (e) {}
      if (isMobile) setShowSidebar(true);
      return;
    }

    if (activeSettingSubsection) {
      setActiveSettingSubsection(null);
      try { appStorage.removeItem("active_setting_subsection"); } catch (e) {}
      if (isMobile) {
        // Mobile: leave Settings in one step (avoid Back only clearing highlight)
        setActiveSection("Personal Info");
        try { appStorage.setItem("active_section", "Personal Info"); } catch (e) {}
        setShowSidebar(true);
      }
      // Desktop: stay on Settings with no selection (existing behavior)
      return;
    }

    setActiveSection("Personal Info");
    try { appStorage.setItem("active_section", "Personal Info"); } catch (e) {}
    if (isMobile) setShowSidebar(true);
  }, [activePermissionSubTab, activeIntroductionSubTab, activeSettingSubsection, isMobile]);

  const handleSettingSubsectionClick = useCallback(
    (subsection) => {
      if (isPostSignupIntroActive() && subsection !== "Introduction") {
        return;
      }

      setActiveSettingSubsection(subsection);
      try { appStorage.setItem("active_setting_subsection", subsection); } catch (e) {}

      if (subsection === "Permission") {
        // Mobile: show Permission menu only. Desktop: auto-open first section (existing behavior).
        if (isMobile) {
          setActivePermissionSubTab(null);
          try { appStorage.removeItem("active_permission_subtab"); } catch (e) {}
        } else {
          setActivePermissionSubTab(PERMISSION_SECTIONS[0]);
          try { appStorage.setItem("active_permission_subtab", PERMISSION_SECTIONS[0]); } catch (e) {}
        }
        setActiveIntroductionSubTab(null);
        try { appStorage.removeItem("active_introduction_subtab"); } catch (e) {}
        // Stay on sidebar for Permission menu on mobile
        return;
      }

      setActivePermissionSubTab(null);
      try { appStorage.removeItem("active_permission_subtab"); } catch (e) {}
      setActiveIntroductionSubTab(null);
      try { appStorage.removeItem("active_introduction_subtab"); } catch (e) {}

      if (isMobile) setShowSidebar(false);
    },
    [isMobile]
  );

  const handleIntroductionSubTabChange = useCallback(
    (subTab) => {
      setActiveIntroductionSubTab(subTab);
      if (subTab) {
        try { appStorage.setItem("active_introduction_subtab", subTab); } catch (e) {}
      } else {
        try { appStorage.removeItem("active_introduction_subtab"); } catch (e) {}
      }
      if (isMobile) setShowSidebar(false);
    },
    [isMobile]
  );

  const handlePermissionSubTabClick = useCallback(
    (subTab) => {
      setActivePermissionSubTab(subTab);
      try { appStorage.setItem("active_permission_subtab", subTab); } catch (e) {}
      if (isMobile) setShowSidebar(false);
    },
    [isMobile]
  );

  const toggleSidebar = useCallback(() => setShowSidebar((prev) => !prev), []);

  const clearProfileCompletionLock = useCallback(() => {
    setProfileCompletionLock({ enabled: false, section: null, personalInfoTarget: null });
    try {
      appStorage.removeItem("pending_profile_completion_step");
    } catch (e) {
      // ignore
    }
  }, []);

  const handleProfileSave = useCallback(async () => {
    setRefreshTrigger((prev) => prev + 1);
    try { window.dispatchEvent(new CustomEvent("phonebook:profile-completion-refresh")); } catch (e) {}

    try {
      const token = Cookies.get("auth_token");
      if (!token) return;
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const profile = data?.data?.[0]?.profile || data?.profile || {};
      if (isProfileSetupComplete(profile)) {
        clearProfileCompletionLock();
        if (shouldRedirectToContactAfterProfile()) {
          clearPostSignupAwaitContact();
          navigate("/contact", { replace: true });
        }
      }
    } catch (e) {
      // ignore — fetchData refresh will reconcile lock state
    }
  }, [clearProfileCompletionLock, navigate]);

  const handleMandatoryStepClick = useCallback(
    (step) => {
      setProfileCompletionLock({
        enabled: true,
        section: step.section,
        personalInfoTarget: step.personalInfoTarget || null,
      });
      setActiveSection(step.section);
      try { appStorage.setItem("active_section", step.section); } catch (e) {}
      setActivePermissionSubTab(null);
      try { appStorage.removeItem("active_permission_subtab"); } catch (e) {}
      setActiveIntroductionSubTab(null);
      try { appStorage.removeItem("active_introduction_subtab"); } catch (e) {}
      setActiveSettingSubsection(null);
      try { appStorage.removeItem("active_setting_subsection"); } catch (e) {}

      if (step.section === "Personal Info") {
        setPersonalInfoFocusTarget((prev) => ({
          section: step.personalInfoTarget || "basic",
          nonce: prev.nonce + 1,
        }));
      }

      if (isMobile) {
        setShowSidebar(false);
      }
    },
    [isMobile]
  );

  const handleOpenProfileGallery = useCallback(() => {
    setCurrentProfileImageIndex(0);
    setShowProfileGalleryModal(true);
  }, []);

  const handleCloseProfileGallery = useCallback(() => {
    setShowProfileGalleryModal(false);
  }, []);

  const handlePrevProfileImage = useCallback(() => {
    setCurrentProfileImageIndex((prev) => {
      if (profileGalleryImages.length <= 1) return 0;
      return (prev - 1 + profileGalleryImages.length) % profileGalleryImages.length;
    });
  }, [profileGalleryImages.length]);

  const handleNextProfileImage = useCallback(() => {
    setCurrentProfileImageIndex((prev) => {
      if (profileGalleryImages.length <= 1) return 0;
      return (prev + 1) % profileGalleryImages.length;
    });
  }, [profileGalleryImages.length]);

  const profilePreviewUrl = profileGalleryImages[currentProfileImageIndex]?.url || userInfo?.avatar || DEFAULT_USER_INFO.avatar;

  const fetchProfileGalleryImages = useCallback(async () => {
    try {
      setProfileGalleryLoading(true);
      const token = Cookies.get("auth_token");

      if (!token) {
        toast.dismiss();
        toast.error("Session expired. Please log in again.");
        return (window.location.href = "/login");
      }

      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile/images`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (data?.status === "1") {
        const latestProfileData = data?.data?.[0] || data?.profile || null;
        const images = normalizeGalleryImages(latestProfileData);
        setProfileGalleryImages(images.length > 0 ? images : [{ url: DEFAULT_USER_INFO.avatar, tags: [] }]);
        setUserInfo((prev) => ({
          ...prev,
          profileImages: images,
          avatar: images[0]?.url || prev.avatar,
        }));
      } else {
        toast.error(data?.message || "Failed to load profile images");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to load profile images");
    } finally {
      setProfileGalleryLoading(false);
    }
  }, []);

  const handleProfileTouchStart = useCallback((e) => {
    touchStartXRef.current = e.changedTouches?.[0]?.clientX ?? null;
  }, []);

  const handleProfileTouchEnd = useCallback((e) => {
    const startX = touchStartXRef.current;
    const endX = e.changedTouches?.[0]?.clientX ?? null;
    touchStartXRef.current = null;

    if (startX === null || endX === null) return;
    const delta = endX - startX;
    if (Math.abs(delta) < 40) return;

    if (delta > 0) handlePrevProfileImage();
    else handleNextProfileImage();
  }, [handlePrevProfileImage, handleNextProfileImage]);

  useEffect(() => {
    if (profileGalleryImages.length <= 1) return undefined;
    const intervalId = setInterval(() => {
      setCurrentProfileImageIndex((prev) => (prev + 1) % profileGalleryImages.length);
    }, 10000);
    return () => clearInterval(intervalId);
  }, [profileGalleryImages.length]);

  useEffect(() => {
    if (showProfileGalleryModal) {
      fetchProfileGalleryImages();
    }
  }, [showProfileGalleryModal, fetchProfileGalleryImages]);

  useEffect(() => {
    if (!showProfileGalleryModal) return;
    setCurrentProfileImageIndex(0);
  }, [showProfileGalleryModal, profileGalleryImages]);

  useEffect(() => {
    if (!showProfileGalleryModal) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") handleCloseProfileGallery();
      if (event.key === "ArrowLeft") handlePrevProfileImage();
      if (event.key === "ArrowRight") handleNextProfileImage();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showProfileGalleryModal, handleCloseProfileGallery, handlePrevProfileImage, handleNextProfileImage]);

  // Fetch profile
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingProfile(true);
        const token = Cookies.get("auth_token");

        if (!token) {
          toast.dismiss();
          toast.error("Session expired. Please log in again.");
          return (window.location.href = "/login");
        }

        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const profile = data?.data?.[0]?.profile || data?.profile || {};
        const latestFlags = getPlanFlagsFromProfile(profile);

        setUserInfo((prev) => ({
          ...prev,
          name: `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || prev.name,
          phone: profile.phone_number
            ? `${profile.country_code || ""}${profile.phone_number}`
            : prev.phone,
          email: profile.email || prev.email,
          avatar: profile.profile_image
            ? `${process.env.REACT_APP_API_BASE_URL}${profile.profile_image}`
            : prev.avatar,
          rawProfile: profile || null,
          // FIX #1: store user_id from profile so VoiceSendModal receives it
          user_id: profile.user_id || profile.id || prev.user_id,
          profileImages: normalizeProfileImages(profile),
        }));
        setProfileGalleryImages(normalizeGalleryImages(profile));
        setPlanCompletionFlags(latestFlags);
        if (isProfileSetupComplete(profile)) {
          clearProfileCompletionLock();
        }
      } catch (err) {
        toast.dismiss();
        toast.error(err?.message || "Failed to fetch profile");
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchData();
  }, [refreshTrigger, clearProfileCompletionLock]);

  // FIX #6: removed dead `currentSections` variable
  // NotesPage needs the modal state so it can refresh after the voice modal closes.
  const activeComponent = useMemo(() => {
    const componentMap = {
    "Personal Info": <PersonalInfoPage onSave={handleProfileSave} forceOpenSection={personalInfoFocusTarget} completionLock={profileCompletionLock} onRequestOpenCompletionModal={() => { try { window.dispatchEvent(new CustomEvent("phonebook:profile-completion-open")); } catch (e) {} }} />,
      Address: <AddressPage />,
      "Work Experience": <WorkExperiencePage onSave={handleProfileSave} />,
      Education: <EducationPage onSave={handleProfileSave} />,
      Payment: <PaymentPage />,
      // Storage: <StoragePage />,//
      // "Storage": <ContactLimitPage />,
      Status: <StatusPage />,
      Settings: (
        <SettingsContent
          activeTab={activeSettingSubsection}
          permissionSubTab={activePermissionSubTab}
          onPermissionSubTabClick={handlePermissionSubTabClick}
          introductionSubTab={activeIntroductionSubTab}
          onIntroductionSubTabChange={handleIntroductionSubTabChange}
        />
      ),
      Notes: <NotesPage isVoiceModalOpen={isVoiceModalOpen} setIsVoiceModalOpen={setIsVoiceModalOpen} />,
    };
    return componentMap[activeSection] || <PersonalInfoPage onSave={handleProfileSave} onRequestOpenCompletionModal={() => { try { window.dispatchEvent(new CustomEvent("phonebook:profile-completion-open")); } catch (e) {} }} />;
  }, [
    activeSection,
    activeSettingSubsection,
    activePermissionSubTab,
    activeIntroductionSubTab,
    personalInfoFocusTarget,
    profileCompletionLock,
    isVoiceModalOpen,
    handleProfileSave,
    handlePermissionSubTabClick,
    handleIntroductionSubTabChange,
  ]);

  useEffect(() => {
    const handleStepEvent = (event) => {
      const step = event?.detail;
      if (!step) return;
      handleMandatoryStepClick(step);
    };

    const handleOpenEvent = () => {
      try {
        window.dispatchEvent(new CustomEvent("phonebook:profile-completion-refresh"));
      } catch (e) {}
    };

    const handleRefreshEvent = () => {
      setRefreshTrigger((prev) => prev + 1);
    };

    const pendingStepRaw = appStorage.getItem("pending_profile_completion_step");
    if (pendingStepRaw) {
      try {
        const pendingStep = JSON.parse(pendingStepRaw);
        appStorage.removeItem("pending_profile_completion_step");
        if (pendingStep) handleMandatoryStepClick(pendingStep);
      } catch (e) {
        appStorage.removeItem("pending_profile_completion_step");
      }
    }

    const handleSidebarFocusEvent = () => handleProfileSidebarFocus();

    const handleOpenIntroductionOnboarding = () => {
      setActiveSection("Settings");
      setActiveSettingSubsection("Introduction");
      setActiveIntroductionSubTab(null);
      setActivePermissionSubTab(null);
      setShowSidebar(true);
      try {
        appStorage.setItem("active_section", "Settings");
        appStorage.setItem("active_setting_subsection", "Introduction");
        appStorage.removeItem("active_introduction_subtab");
        appStorage.removeItem("active_permission_subtab");
      } catch (e) {
        // ignore
      }
    };

    const handlePostSignupGoProfiles = () => {
      // After Introduction Skip: go to Personal Info in locked edit mode
      handleMandatoryStepClick({
        key: "personal_info",
        section: "Personal Info",
        route: "/personal-info",
        personalInfoTarget: "basic",
      });
    };

    // If landing on personal-info already mid post-signup intro
    if (isPostSignupIntroActive()) {
      handleOpenIntroductionOnboarding();
    }

    window.addEventListener("phonebook:profile-completion-step", handleStepEvent);
    window.addEventListener("phonebook:profile-completion-open", handleOpenEvent);
    window.addEventListener("phonebook:profile-completion-refresh", handleRefreshEvent);
    window.addEventListener("phonebook:profile-sidebar-focus", handleSidebarFocusEvent);
    window.addEventListener(OPEN_INTRODUCTION_ONBOARDING_EVENT, handleOpenIntroductionOnboarding);
    window.addEventListener("phonebook:post-signup-go-profiles", handlePostSignupGoProfiles);

    return () => {
      window.removeEventListener("phonebook:profile-completion-step", handleStepEvent);
      window.removeEventListener("phonebook:profile-completion-open", handleOpenEvent);
      window.removeEventListener("phonebook:profile-completion-refresh", handleRefreshEvent);
      window.removeEventListener("phonebook:profile-sidebar-focus", handleSidebarFocusEvent);
      window.removeEventListener(OPEN_INTRODUCTION_ONBOARDING_EVENT, handleOpenIntroductionOnboarding);
      window.removeEventListener("phonebook:post-signup-go-profiles", handlePostSignupGoProfiles);
    };
  }, [handleMandatoryStepClick, handleProfileSidebarFocus, isMobile]);

  const isSettingsMode = activeSection === "Settings";

  const sidebarSections = useMemo(() => {
    if (!isSettingsMode) return SECTIONS;
    if (activeSettingSubsection === "Permission") return PERMISSION_SECTIONS;
    return SETTINGS_SUBSECTIONS;
  }, [isSettingsMode, activeSettingSubsection]);

  const isPermissionListVisible = isSettingsMode && activeSettingSubsection === "Permission";

  const sidebarClasses = `${showSidebar ? "block" : "hidden"} min-[900px]:block w-full min-[900px]:w-[360px] min-[900px]:min-w-[300px] min-[900px]:flex-shrink-0 space-y-4 h-full min-h-0`;
  const mainContentClasses = `${!showSidebar ? "block" : "hidden min-[900px]:block"} relative w-full min-[900px]:flex-1 min-w-0 min-h-0 h-full flex flex-col overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden`;
  const qrPath = userInfo?.rawProfile?.phone_qrcode_url || "";
  const qrImageUrl = qrPath
    ? (String(qrPath).startsWith("http")
        ? qrPath
        : `${process.env.REACT_APP_API_BASE_URL}${qrPath}`)
    : "";

  const handleDownloadQr = useCallback(async () => {
    if (!qrImageUrl) {
      toast.error("QR code image not available.");
      return;
    }

    try {
      setDownloadingQr(true);
      const token = Cookies.get("auth_token");
      const res = await fetch(qrImageUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) {
        throw new Error("Failed to download QR code");
      }

      const blob = await res.blob();
      const extension = (blob.type?.split("/")?.[1] || "png").replace("jpeg", "jpg");
      const fileName = `phonebook-qr-code.${extension}`;
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success("QR code downloaded");
    } catch (err) {
      toast.error(err?.message || "Failed to download QR code");
    } finally {
      setDownloadingQr(false);
    }
  }, [qrImageUrl]);

  return (
    <div className="h-full min-h-0 overflow-hidden bg-[#F3F2FF] p-2 sm:p-4 md:p-6">
      <div className="mx-auto h-full min-h-0">
        {!showSidebar && <MenuButton onToggleSidebar={toggleSidebar} />}

        {loadingProfile || paymentStatusLoading ? (
          <ProfileSkeleton />
        ) : (
          <div className="flex h-full min-h-0 gap-2 sm:gap-4">
            <div className={`${sidebarClasses} overflow-y-auto [&::-webkit-scrollbar]:hidden`}>
              {isSettingsMode ? (
                <BackButton
                  onBack={handleBack}
                  label={
                    activePermissionSubTab
                      ? "Permission"
                      : activeIntroductionSubTab
                      ? "Introduction"
                      : "Settings"
                  }
                />
              ) : (
                <UserProfile
                  userInfo={userInfo}
                  profilePreviewUrl={profilePreviewUrl}
                  onUpload={handleUploadProfileImage}
                  uploading={uploadingImage}
                  handleSectionClick={handleSectionClick}
                  planType={planType}
                  onOpenImageModal={() => setShowImageModal(true)}
                  onOpenQrModal={() => setShowQrModal(true)}
                  onOpenProfileGallery={handleOpenProfileGallery}
                />
              )}
              <div className="space-y-3 px-2 sm:px-0">
                {sidebarSections.map((section) => (
                  <SectionItem
                    key={section}
                    label={isSettingsMode ? getSectionDisplayLabel(section) : section}
                    icon={getSectionIcon(section)}
                    active={
                      isPermissionListVisible
                        ? activePermissionSubTab === section
                        : isSettingsMode
                        ? activeSettingSubsection === section
                        : activeSection === section
                    }
                    disabled={
                      (isPostSignupIntroActive() &&
                        isSettingsMode &&
                        section !== "Introduction") ||
                      (profileCompletionLock.enabled &&
                      profileCompletionLock.section !== section)
                    }
                    onClick={
                      isPermissionListVisible
                        ? () => handlePermissionSubTabClick(section)
                        : isSettingsMode
                        ? () => handleSettingSubsectionClick(section)
                        : () => handleSectionClick(section)
                    }
                  />
                ))}
                {isSettingsMode && !activePermissionSubTab && (
                  <>
                    {/* <button
                      type="button"
                      onClick={openAppTour}
                      className="group flex w-full items-center gap-3 rounded-2xl border border-[#EEE6FF] bg-gradient-to-r from-[#FAF8FF] to-white px-4 py-3.5 text-left transition hover:border-[#D6C5FF] hover:shadow-[0_8px_24px_rgba(108,69,255,0.1)]"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#8A6CFF] to-[#6C45FF] text-white shadow-sm">
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <circle cx="12" cy="12" r="9" />
                          <path d="M12 8v4l2.5 2.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-bold text-[#1E1F29]">Replay App Tour</span>
                        <span className="block text-xs text-[#7C8090]">Walk through  PhoneBookPlus features again</span>
                      </span>
                      <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-[#6C45FF] transition group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M9 6l6 6-6 6" />
                      </svg>
                    </button> */}
                    <button
                      type="button"
                      className="flex items-center gap-2 px-2 text-sm font-medium text-red-600 transition hover:text-red-700 sm:px-0"
                      onClick={() => setShowDeleteModal(true)}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-5 w-5 shrink-0"
                        aria-hidden="true"
                      >
                        <path d="M4 7h16" />
                        <path d="M9 7V4h6v3" />
                        <path d="M6.5 7l1 13h9l1-13" />
                        <path d="M10 11v5M14 11v5" />
                      </svg>
                      Delete Account
                    </button>
                  </>
                )}
              </div>
            </div>

            <div ref={profileContentPanelRef} className={mainContentClasses}>
              {activeComponent}
              {isVoiceModalOpen && (
                <VoiceSendModal
                  open={isVoiceModalOpen}
                  onClose={() => setIsVoiceModalOpen(false)}
                  userId={userInfo?.user_id}
                  onSave={() => setIsVoiceModalOpen(false)}
                  isProfileNote={true}
                  contentPanelRef={profileContentPanelRef}
                />
              )}
            </div>
          </div>
        )}
      </div>

      <DeleteAccountModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        loading={deletingAccount}
      />

      {/* NEW: multi-image upload modal — only shown for plan type 2 & 3 */}
      <ImageUploadModal
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        onUpload={handleUploadProfileImage}
        uploading={uploadingImage}
        onImagesChange={handleProfileImagesChange}
      />

      <ProfileImageSliderModal
        open={showProfileGalleryModal}
        images={profileGalleryImages}
        currentIndex={currentProfileImageIndex}
        onClose={handleCloseProfileGallery}
        onPrev={handlePrevProfileImage}
        onNext={handleNextProfileImage}
        onTouchStart={handleProfileTouchStart}
        onTouchEnd={handleProfileTouchEnd}
        loading={profileGalleryLoading}
      />

      {showQrModal && (
        <div
          className="fixed inset-0 z-[22000] bg-black/45 flex items-center justify-center p-4"
          onClick={() => setShowQrModal(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">My QR Code</h3>
              <button
                type="button"
                onClick={() => setShowQrModal(false)}
                className="h-8 w-8"
                aria-label="Close"
              >
                <img src="../chatbox/close.svg" alt="Close" className="h-4 w-4" />
              </button>
            </div>

            {qrImageUrl ? (
              <>
                <div className="rounded-xl border border-[#E6E2FF] p-4 bg-white flex items-center justify-center">
                  <img
                    src={qrImageUrl}
                    alt="Phone QR Code"
                    className="w-64 h-64 object-contain"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleDownloadQr}
                  disabled={downloadingQr}
                  className="submit-button w-full mt-4 disabled:opacity-60"
                >
                  {downloadingQr ? "Downloading..." : "Download QR Code"}
                </button>
              </>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
                QR code image not available.
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
